<template>
  <button
    type="button"
    class="btn flex active:not-disabled:scale-95 disabled:cursor-not-allowed"
    :class="[...buttonClasses, $attrs.class]"
    :disabled="disabled"
    :title="tooltip ?? title"
  >
    <span class="leading-6 empty:hidden">
      <FontAwesomeIcon
        v-if="displayLoader"
        :icon="['fal', 'spinner-third']"
        spin
        :size="iconSizeClass"
      />
      <FontAwesomeIcon
        v-else-if="selectedIcon"
        :icon="selectedIcon"
        :size="iconSizeClass"
      />
    </span>
    <span
      v-if="title && !hideTitle"
      class="leading-6 whitespace-nowrap capitalize-first"
      :class="titleVisibilityClass"
    >
      {{ title }}
    </span>
    <slot name="content_inside" />
  </button>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'BaseButton',
  components: { FontAwesomeIcon },
  props: {
    iconType: {
      type: String,
      required: false,
      default: 'fal'
    },
    className: {
      type: String,
      required: false,
      default: null
    },
    // Crema: native tooltip text (the former package wrapped the button in Tippy).
    tooltip: {
      type: String,
      required: false,
      default: null
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    hideTitle: {
      type: Boolean,
      required: false,
      default: false
    },
    icon: {
      type: String,
      required: false,
      default: null
    },
    linkTarget: {
      type: Object,
      required: false,
      default: function () {
        return {}
      }
    },
    color: {
      type: String,
      required: false,
      default: '',
      validator: function (value) {
        const possibleTypes = [
          '',
          'primary',
          'secondary',
          'success',
          'warning',
          'danger',
          'dark',
          'info',
          'gray'
        ]
        return possibleTypes.indexOf(value) !== -1
      }
    },
    themeColor: {
      type: String,
      required: false,
      default: null
    },
    isOutline: {
      type: Boolean,
      required: false,
      default: false
    },
    isRounded: {
      type: Boolean,
      required: false,
      default: false
    },
    isSoft: {
      type: Boolean,
      required: false,
      default: false
    },
    isElevated: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
      validator: function (value) {
        return (
          ['extra-small', 'small', 'medium', 'large', 'extra-large'].indexOf(
            value
          ) !== -1
        )
      }
    },
    scopedClasses: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'button',
      required: false,
      validator: function (value) {
        return ['button', 'submit'].indexOf(value) !== -1
      }
    },
    cursor: {
      type: String,
      required: false,
      default: 'pointer',
      validator: (val) =>
        [
          'pointer',
          'default',
          'move',
          'text',
          'help',
          'not-allowed',
          'none',
          'context-menu',
          'grabbing'
        ].indexOf(val) !== -1
    },
    textColor: { type: String, required: false, default: null },
    disabled: { type: Boolean, required: false, default: false },
    inverseIconOrder: {
      type: Boolean,
      required: false,
      default: false
    },
    displayLoader: {
      type: Boolean,
      required: false,
      default: false
    },
    block: {
      type: Boolean,
      required: false,
      default: false
    },
    secondaryColor: {
      type: Boolean,
      required: false,
      default: false
    },
    printIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    hideTitleUntil: {
      type: String,
      required: false,
      default: null,
      validator: (value) => [null, 'xl', 'xxl', 'custom'].includes(value)
    }
  },
  computed: {
    selectedIcon() {
      if (this.icon) {
        return [this.iconType, this.icon]
      }
      switch (true) {
        case this.printIcon:
          return ['fal', 'print']
      }
      return undefined
    },
    hasTitle() {
      return this.title && this.title.length
    },
    buttonCursorClass() {
      return `cursor-${this.cursor}`
    },
    buttonRoundedClass() {
      return this.isRounded ? 'rounded-full' : ''
    },
    buttonTypeClass() {
      // if (this.themeColor) {
      //   return `btn ${this.themeColor}`
      // }
      // if (this.isOutline) {
      //   return `btn-outline-${this.color}`
      // }
      // if (this.isSoft) {
      //   return `btn-${this.color}-soft`
      // }
      // return this.color === 'primary' ? 'btn bg-primary' : `btn-${this.color}`
    },
    buttonColorClass() {
      if (this.secondaryColor) {
        return 'bg-secondary border-secondary font-medium text-contrast-secondary'
      }
      if (this.themeColor) {
        return this.themeColor
      }
      if (this.isOutline) {
        switch (this.color) {
          case 'gray':
            return 'bg-white border-gray-500 text-gray-500 font-medium'
          case 'secondary':
            return 'bg-white border-secondary text-slate-700 font-medium'
          case 'primary':
            return 'bg-white border-primary text-primary-700 font-medium'
          case 'warning':
            return 'bg-white border-yellow-400 border-yellow-400 text-slate-700 font-medium'
          case 'success':
            return 'bg-white border-lime-500 text-lime-500 font-medium'
          case 'danger':
            return 'bg-white border-primary-500 text-primary-500 font-medium hover:bg-primary-50'
          case 'dark':
            return 'bg-white border-gray-800 text-slate-700 font-medium'
          case 'light':
            return 'bg-white border-gray-100 text-slate-700 font-medium'
          case 'orange':
            return 'bg-white border-orange-100 text-slate-700 font-medium'
          case 'green':
            return 'bg-white border-green-600 text-green-700 font-medium'
          default:
            return 'border-primary font-medium'
        }
      }
      switch (this.color) {
        case 'gray':
          return 'bg-gray-500 border-gray-500 text-white'
        case 'secondary':
          return 'border-secondary bg-secondary text-contrast-secondary'
        case 'warning':
          return 'border-yellow-400 bg-yellow-400 dark:border-yellow-400;'
        case 'success':
          return 'bg-lime-500 border-lime-500 text-white dark:border-lime-500'
        case 'danger':
          return 'bg-primary-500 border-primary-500 text-white hover:bg-primary-600 dark:border-primary-500'
        case 'dark':
          return 'bg-gray-800 border-gray-800 text-white dark:border-gray-700 dark:bg-gray-700 dark:text-slate-300'
        case 'light':
          return 'bg-gray-100 border-gray-100 text-slate-800 dark:border-gray-700 dark:bg-gray-700 dark:text-slate-300'
        case 'orange':
          return 'bg-orange-300 border-orange-100 text-white-800 dark:border-gray-700 dark:bg-gray-700 dark:text-slate-300'
        case 'green':
          return 'bg-green-600  border-green-600 text-white-800 hover:bg-green-700 dark:border-gray-700 dark:bg-gray-700 dark:text-slate-300'
        default:
          return 'border-primary bg-primary text-contrast-primary'
      }
    },
    buttonSizeClass() {
      switch (this.size) {
        case 'extra-small':
          return 'px-1.5 py-0 gap-1.5 text-xs'
        case 'small':
          return 'px-2 py-0.5 gap-1.5'
        case 'large':
          return 'px-3 py-3 gap-2'
        case 'extra-large':
          return 'px-4 py-4 gap-2 text-base'
        case 'medium':
        default:
          return 'px-2 py-1.5 gap-2'
      }
    },
    iconSizeClass() {
      switch (this.size) {
        case 'extra-small':
          return 'sm'
        case 'large':
          return 'xl'
        case 'extra-large':
          return 'xl'
        case 'small':
        case 'medium':
        default:
          return undefined
      }
    },
    buttonIconPosition() {
      return this.inverseIconOrder ? 'flex-row' : 'flex-row-reverse'
    },
    buttonElevationClass() {
      return this.isElevated ? 'shadow-md' : ''
    },
    buttonBlockClass() {
      return this.block ? 'w-full' : ''
    },
    buttonTextColorClass() {
      if (!this.isOutline) {
        return this.textColor ? `text-${this.textColor}` : ''
      } else {
        return `text-theme-${this.color} hover:text-white`
      }
    },
    buttonHeight() {
      if (this.hasTitle) return ''

      switch (this.size) {
        case 'extra-small':
          return 'h-6 px-6'
        default:
          return 'h-8 px-8'
      }
    },
    buttonClasses() {
      return [
        this.className,
        this.buttonTypeClass,
        this.buttonRoundedClass,
        this.buttonElevationClass,
        this.buttonSizeClass,
        this.scopedClasses,
        this.buttonCursorClass,
        this.buttonTextColorClass,
        this.buttonIconPosition,
        this.buttonColorClass,
        this.buttonBlockClass
        // this.buttonHeight
      ]
    },
    titleVisibilityClass() {
      switch (this.hideTitleUntil) {
        case 'xl':
          return 'hidden xl:inline-block'
        case 'xxl':
          return 'hidden xxl:inline-block'
        case 'custom':
          return 'hidden min-[1600px]:inline-block'
        default:
          return 'inline-block'
      }
    }
  }
}
</script>
